// =============================================================================
// 2120, Phase 44: Onboarding Pool Welcome Banner
// =============================================================================
// Calm, one-time celebratory banner that appears on the Import page when a
// freshly-converted paying customer first sees their onboarding pool. The
// banner reinforces the conversion-reward framing:
//
//   "Welcome to Horizon. Your plan includes 100 setup imports to help build
//    your complete financial history."
//
// Dismissal is server-side (POST /api/billing/onboarding-pool/welcome-seen)
// so it doesn't reappear across devices. Until the API confirms, the banner
// is also hidden locally so a slow network doesn't flash it twice.
// =============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABEL = {
  clarity: 'Clarity',
  horizon: 'Horizon',
  summit:  'Summit',
};

export default function OnboardingPoolWelcomeBanner({ session, pool, plan, welcomeSeenAt, onDismissed }) {
  const [dismissing, setDismissing] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);

  // Reset local dismissal if the underlying pool/profile changes for any
  // reason (e.g., admin reactivates a pool on a different user). Cheap
  // safety net; in practice this only fires once per user.
  useEffect(() => {
    setLocalDismissed(false);
  }, [pool?.activated_at]);

  const visible =
    !!pool &&
    pool.active &&
    !welcomeSeenAt &&
    !localDismissed &&
    PLAN_LABEL[plan];

  if (!visible) return null;

  const handleDismiss = async () => {
    setDismissing(true);
    setLocalDismissed(true);
    try {
      if (session?.access_token) {
        await axios.post(
          `${BACKEND_URL}/api/billing/onboarding-pool/welcome-seen`,
          {},
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
      }
    } catch (e) {
      // Best-effort, the local dismissal already hid the banner; we'll
      // retry the server-side flag automatically on the next page load
      // if it didn't go through.
    } finally {
      onDismissed && onDismissed();
      setDismissing(false);
    }
  };

  return (
    <Card
      data-testid="onboarding-pool-welcome-banner"
      className="rounded-2xl border-[hsl(var(--gold))]/40 bg-gradient-to-br from-[hsl(var(--gold))]/10 via-[hsl(var(--gold))]/5 to-transparent overflow-hidden"
    >
      <CardContent className="p-5 flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-[hsl(var(--gold))]/15 grid place-items-center shrink-0 ring-1 ring-[hsl(var(--gold))]/30">
          <Sparkles className="h-5 w-5 text-[hsl(var(--gold))]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-semibold tracking-tight">
            Welcome to {PLAN_LABEL[plan]}.
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Your plan includes <strong className="text-foreground">{pool.total} setup imports</strong> to help you build your complete financial history.
            The clock starts on your first upload, and you have <strong className="text-foreground">30 days</strong> to use them.
          </p>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="rounded-full bg-background/60 backdrop-blur px-2 py-0.5 ring-1 ring-border/60">
              {pool.total - pool.used} available
            </span>
            {pool.days_left != null && (
              <span className="rounded-full bg-background/60 backdrop-blur px-2 py-0.5 ring-1 ring-border/60">
                {pool.days_left} days left
              </span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          disabled={dismissing}
          className="rounded-xl shrink-0 -mr-2 -mt-2 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss welcome banner"
          data-testid="onboarding-pool-welcome-dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
